import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FaStar, FaCommentDots } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  createConsultantAssignmentFeedback,
  listConsultantAssignments,
  submitConsultantAssignmentFeedback,
  updateConsultantAssignmentFeedback,
} from "../../../api/consultantPortalApi";
import {
  PageHeader,
  Panel,
  StatusBadge,
  EmptyState,
} from "../../../components/Portal/ui";

// Status -> Chakra colour scheme for StatusBadge dots (registered scales only).
const STATUS_SCHEME = {
  draft: "gray",
  submitted: "green",
};

const AssignmentFeedback = () => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({});

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listConsultantAssignments();
      const items = Array.isArray(response?.items) ? response.items : [];
      setAssignments(items);
      setDrafts((current) => {
        const next = { ...current };
        items.forEach((assignment) => {
          assignment.feedbackCycles?.forEach((cycle) => {
            const key = `${assignment.matchId}:${cycle.cycleStartDate}`;
            if (!next[key]) {
              next[key] = {
                rating: cycle.rating || 0,
                comment: cycle.comment || "",
              };
            }
          });
        });
        return next;
      });
    } catch (error) {
      toast({
        title: t("consultantPortal.assignmentFeedback.toasts.loadFailedTitle"),
        description: error?.response?.data?.detail || t("consultantPortal.assignmentFeedback.toasts.loadFailedDescription"),
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const allCycles = useMemo(() => {
    return assignments.flatMap((assignment) =>
      (assignment.feedbackCycles || []).map((cycle) => ({
        assignment,
        cycle,
        key: `${assignment.matchId}:${cycle.cycleStartDate}`,
      }))
    );
  }, [assignments]);

  const assignmentsWithNoCycles = useMemo(
    () => assignments.filter((a) => !(a.feedbackCycles?.length > 0)),
    [assignments]
  );

  const handleSave = useCallback(
    async ({ assignment, cycle, submitAfterSave = false }) => {
      const key = `${assignment.matchId}:${cycle.cycleStartDate}`;
      const draft = drafts[key] || { rating: 0, comment: "" };
      setSavingKey(key);
      try {
        let feedbackId = cycle.feedbackId;
        if (feedbackId) {
          const updated = await updateConsultantAssignmentFeedback(feedbackId, {
            rating: draft.rating || null,
            comment: draft.comment || null,
          });
          feedbackId = updated.id;
        } else {
          const created = await createConsultantAssignmentFeedback({
            matchId: assignment.matchId,
            cycleStartDate: cycle.cycleStartDate,
            rating: draft.rating || null,
            comment: draft.comment || null,
          });
          feedbackId = created.id;
        }

        if (submitAfterSave) {
          await submitConsultantAssignmentFeedback(feedbackId);
        }

        toast({
          title: submitAfterSave ? t("consultantPortal.assignmentFeedback.toasts.submitted") : t("consultantPortal.assignmentFeedback.toasts.saved"),
          status: "success",
        });
        fetchAssignments();
      } catch (error) {
        toast({
          title: t("consultantPortal.assignmentFeedback.toasts.saveFailedTitle"),
          description: error?.response?.data?.detail || t("consultantPortal.assignmentFeedback.toasts.saveFailedDescription"),
          status: "error",
        });
      } finally {
        setSavingKey(null);
      }
    },
    [drafts, fetchAssignments, toast, t]
  );

  const cycleTitle = (assignment) =>
    assignment.clientName || t("consultantPortal.assignmentFeedback.clientAssignment");

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="CP"
        eyebrow={t("consultantPortal.common.eyebrow", "Consultant Portal")}
        title={t("consultantPortal.assignmentFeedback.pageTitle")}
      />

      <Box p="20px">
        {loading ? (
          <Flex minH="320px" align="center" justify="center">
            <Spinner size="lg" color="portal.primary" />
          </Flex>
        ) : assignments.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<FaCommentDots />}
              title={t("consultantPortal.assignmentFeedback.empty")}
              hint={t("consultantPortal.assignmentFeedback.emptyHint", "Feedback cycles will appear here once you are on an assignment.")}
            />
          </Panel>
        ) : (
          <>
            {assignmentsWithNoCycles.length > 0 ? (
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="12px" mb="12px">
                {assignmentsWithNoCycles.map((assignment) => (
                  <Panel
                    key={`no-cycle-${assignment.matchId}`}
                    title={cycleTitle(assignment)}
                  >
                    <Box p="14px">
                      <Text fontWeight="700" fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                        {assignment.needTitle || t("consultantPortal.assignmentFeedback.untitledNeed")}
                      </Text>
                      <Text fontSize="12px" fontWeight="600" color="portal.muted" mt="2px" sx={{ marginBottom: "0 !important" }}>
                        {`${assignment.assignmentStartDate ?? "-"} → ${assignment.assignmentEndDate ?? t("consultantPortal.assignmentFeedback.ongoing")}`}
                      </Text>
                      <Text fontSize="12px" fontWeight="600" color="portal.muted" mt="10px" sx={{ marginBottom: "0 !important" }}>
                        {t("consultantPortal.assignmentFeedback.noCyclesYet")}
                      </Text>
                    </Box>
                  </Panel>
                ))}
              </SimpleGrid>
            ) : null}

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="12px">
              {allCycles.map(({ assignment, cycle, key }) => {
                const draft = drafts[key] || { rating: 0, comment: "" };
                const isSubmitted = cycle.status === "submitted";
                const isSaving = savingKey === key;
                const ratingValue = Number(draft.rating || cycle.rating || 0);
                return (
                  <Panel
                    key={key}
                    title={cycleTitle(assignment)}
                    actions={
                      <StatusBadge
                        label={t(`consultantPortal.assignmentFeedback.statusLabels.${cycle.status}`, cycle.status)}
                        colorScheme={STATUS_SCHEME[cycle.status] || "gray"}
                      />
                    }
                  >
                    <Box p="14px">
                      <Flex justify="space-between" gap="12px" wrap="wrap" mb="14px">
                        <Box minW={0}>
                          <Text fontSize="13px" fontWeight="700" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                            {assignment.needTitle || t("consultantPortal.assignmentFeedback.untitledNeed")}
                          </Text>
                          <Text fontSize="12px" fontWeight="600" color="portal.muted" mt="2px" sx={{ marginBottom: "0 !important" }}>
                            {`${assignment.assignmentStartDate || "-"} → ${assignment.assignmentEndDate || t("consultantPortal.assignmentFeedback.ongoing")}`}
                          </Text>
                        </Box>
                        <VStack align="end" spacing="2px">
                          <Text fontSize="11px" fontWeight="700" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                            {`${cycle.cycleStartDate} → ${cycle.cycleEndDate}`}
                          </Text>
                        </VStack>
                      </Flex>

                      <Text
                        fontSize="11px"
                        fontWeight="800"
                        color="portal.muted"
                        textTransform="uppercase"
                        letterSpacing="0.04em"
                        mb="6px"
                        sx={{ marginBottom: "6px !important" }}
                      >
                        {t("consultantPortal.assignmentFeedback.ratingLabel")}
                      </Text>
                      <HStack mb="14px" spacing="6px">
                        {[1, 2, 3, 4, 5].map((value) => (
                          <IconButton
                            key={value}
                            aria-label={t("consultantPortal.assignmentFeedback.rateStarsAria", { count: value })}
                            icon={<FaStar />}
                            size="sm"
                            colorScheme={value <= ratingValue ? "yellow" : "gray"}
                            variant={value <= ratingValue ? "solid" : "outline"}
                            onClick={() =>
                              setDrafts((current) => ({
                                ...current,
                                [key]: { ...draft, rating: value },
                              }))
                            }
                            isDisabled={isSubmitted}
                          />
                        ))}
                      </HStack>
                      <Textarea
                        rows={5}
                        fontWeight="600"
                        value={draft.comment}
                        onChange={(event) =>
                          setDrafts((current) => ({
                            ...current,
                            [key]: { ...draft, comment: event.target.value },
                          }))
                        }
                        placeholder={t("consultantPortal.assignmentFeedback.commentPlaceholder")}
                        isDisabled={isSubmitted}
                      />
                      {cycle.submittedAt ? (
                        <Text fontSize="12px" fontWeight="600" color="portal.muted" mt="10px" sx={{ marginBottom: "0 !important" }}>
                          {t("consultantPortal.assignmentFeedback.submittedAt", { date: cycle.submittedAt })}
                        </Text>
                      ) : null}
                      <HStack mt="16px" spacing="8px" flexWrap="wrap">
                        {!isSubmitted ? (
                          <>
                            <Button
                              variant="secondary"
                              size="sm"
                              isLoading={isSaving}
                              onClick={() => handleSave({ assignment, cycle, submitAfterSave: false })}
                              isDisabled={!cycle.canSubmit}
                            >
                              {t("consultantPortal.assignmentFeedback.buttons.saveDraft")}
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              isLoading={isSaving}
                              onClick={() => handleSave({ assignment, cycle, submitAfterSave: true })}
                              isDisabled={!cycle.canSubmit}
                            >
                              {t("consultantPortal.assignmentFeedback.buttons.submitFeedback")}
                            </Button>
                          </>
                        ) : (
                          <Text color="green.600" fontWeight="800" fontSize="13px" sx={{ marginBottom: "0 !important" }}>
                            {t("consultantPortal.assignmentFeedback.feedbackSubmitted")}
                          </Text>
                        )}
                      </HStack>
                      {!cycle.canSubmit && !isSubmitted ? (
                        <Text fontSize="12px" fontWeight="600" color="portal.muted" mt="10px" sx={{ marginBottom: "0 !important" }}>
                          {t("consultantPortal.assignmentFeedback.availableEndOfCycle")}
                        </Text>
                      ) : null}
                    </Box>
                  </Panel>
                );
              })}
            </SimpleGrid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AssignmentFeedback;
